<template>
  <div>
    <template v-if="readOnly">
      <div v-if="viewItem() !== null && viewItem() !== undefined"
           class="p-2 italic"
           :class="{
             'text-right w-full': valueIsNumber && singleLine,
             'overflow-hidden text-ellipsis whitespace-nowrap not-italic': singleLine,
           }">
        {{ viewItem() }}
      </div>
      <div v-else class="p-2 italic text-fg-muted">
        (blank)
      </div>
    </template>
    <template v-else>
      <div class="relative flex items-center w-full">
        <AutoExpandingTextarea
          v-if="!readOnly"
          :clearable="header.isNullable"
          :error="fieldError"
          :model-value="modelValue?.toString()"
          @cancel="$emit('update:modelValue', props.modelValue)"
          @change="changed"
          @submit="changed"
        />
      </div>
      <BaseProgressBar v-if="saving" :value="null" class="h-1 m-0 p-0" />
      <div v-else class="h-1 m-0 p-0" />
    </template>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useTableStore } from '@/store/storeTable'
import { useCellSave } from '@/composables/useCellSave'
import { formatDate } from '@/helpers/DateFormatter'
import { useUserPrefFormatting } from '@/composables/useUserPrefFormatting'
import AutoExpandingTextarea from '@/components/BaseComponents/AutoExpandingTextarea.vue'
import BaseProgressBar from '@/components/BaseComponents/BaseProgressBar.vue'

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean, Array, Date, Object],
    default: null
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  singleLine: {
    type: Boolean,
    default: false
  },
  autoSave: {
    type: Boolean,
    default: true
  },
  rowId: { type: [String, Number], default: '' },
  header: { type: Object, required: true },
  onSaveSuccess: { type: Function, default: null }
});

const emit = defineEmits(['update:modelValue'])


// Lokal valideringstilstand (tom-verdi); lagringsfeil eies av useCellSave.
const validationError = ref('')
const { saving, saveErrorMessage, saveCell } = useCellSave()

const fieldError = computed(() => validationError.value || saveErrorMessage.value)

// TableStore
const tableStore = useTableStore()
const { formatNumber, dateFormat } = useUserPrefFormatting()

async function changed (newValue) {
  if (newValue === '' || newValue === null) {
    if (props.header.isNullable) {
      emit('update:modelValue', null)
      if (!props.autoSave) return;
      const item = {
        tableKey: String(props.header?.tableKey || tableStore.tableKey || '').trim(),
        rowId: props.rowId,
        columnName: props.header.field,
        oldValue: props.modelValue,
        newValue: null
      };
      validationError.value = ''
      await saveCell(item, props.onSaveSuccess);
      return;
    } else {
      validationError.value = 'Feltet kan ikke være tomt';
      return;
    }
  }
  emit('update:modelValue', newValue)
  if (!props.autoSave) return;
  const item = {
    tableKey: String(props.header?.tableKey || tableStore.tableKey || '').trim(),
    rowId: props.rowId,
    columnName: props.header.field,
    oldValue: props.modelValue,
    newValue
  };
  validationError.value = ''
  await saveCell(item, props.onSaveSuccess);
}


const valueIsNumber = ref(false)

function viewItem ()
{
  if (props.modelValue) {
    const dates = ['datetime', 'datetime2', 'date', 'time']
    const numbers = ['int', 'bigint', 'decimal', 'numeric']

    if (dates.includes(props.header.basicDataType)) {
      return formatDate(props.modelValue, undefined, dateFormat.value)
    }

    if (numbers.includes(props.header.basicDataType)) {
      const num = Number(props.modelValue)
      valueIsNumber.value = true
      // WI 2086: tallformatering følger brukerens regionale prefs.
      return formatNumber(num)
    }
  }
  return props.modelValue
}
</script>
